/**
 * 📖 HeroCodexEditorPage — owner-only one-page editor for every hero's
 * public codex entry.
 *
 * Supports:
 *   • Class meta: tagline, long description, lore, base_stats (block/charisma)
 *   • Per-skill edits: name, lvl_req, cost, tagline, long_desc
 *   • Reset-to-canon: wipes ALL overrides and restores the baked lore
 *
 * Backend contract:
 *   GET    /api/hero-codex                       → list of all classes
 *   GET    /api/admin/hero-codex/{class_id}       → {canon, merged, overrides}
 *   PATCH  /api/admin/hero-codex/{class_id}       → save class meta
 *   PATCH  /api/admin/hero-codex/{class_id}/skills/{skill_id} → save skill
 *   DELETE /api/admin/hero-codex/{class_id}/overrides         → reset
 *
 * The 'merged' view (canon + overrides applied) drives the UI so what
 * the admin sees matches what the player will see.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Collapse the backend's slot shape ({normal, secondary[], ...}) into
 *  a flat list of {slot, skill} tuples the UI can walk in order. */
function flattenSkills(slots) {
  if (!slots || typeof slots !== "object") return [];
  const order = ["normal", "secondary", "passive", "super_passive", "ultimate", "super_ultimate", "combo_ultimate"];
  const out = [];
  order.forEach((slot) => {
    const v = slots[slot];
    if (!v) return;
    if (Array.isArray(v)) {
      v.forEach((skill, idx) => out.push({ slot, idx, skill }));
    } else if (typeof v === "object") {
      out.push({ slot, idx: null, skill: v });
    }
  });
  return out;
}

export default function HeroCodexEditorPage() {
  const { user, loading } = useAuth();
  const isOwner = user && (user.role === "owner" || (user.email || "").toLowerCase() === "omnimuscle@yahoo.com");
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [codex, setCodex] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  // Per-field drafts so edits don't fire a request per keystroke.
  const [metaDraft, setMetaDraft] = useState({ tagline: "", long_desc: "", lore: "", sprite_url: "", block: "", charisma: "" });
  const [skillDrafts, setSkillDrafts] = useState({}); // {skill_id: {name, lvl_req, cost, tagline, long_desc}}

  // 1️⃣ Load the class picker.
  useEffect(() => {
    document.title = "📖 Hero Codex Editor · Admin · OMNIMUSCLE";
    if (!isOwner) return;
    axios.get(`${API}/hero-codex`, { withCredentials: true }).then(({ data }) => {
      const list = data?.heroes || data?.classes || data;
      setClasses(Array.isArray(list) ? list : []);
      if (Array.isArray(list) && list.length > 0) setClassId(list[0].class_id);
    });
  }, [isOwner]);

  // 2️⃣ Load the full codex for the selected class.
  useEffect(() => {
    if (!classId || !isOwner) return;
    setFetching(true);
    axios.get(`${API}/admin/hero-codex/${classId}`, { withCredentials: true })
      .then(({ data }) => {
        setCodex(data);
        const m = data?.merged || {};
        setMetaDraft({
          tagline:    m.tagline || "",
          long_desc:  m.long_desc || "",
          lore:       m.lore || "",
          sprite_url: m.sprite_url || "",
          block:      (m.base_stats?.block ?? "").toString(),
          charisma:   (m.base_stats?.charisma ?? "").toString(),
        });
        // Seed skill drafts from merged values
        const sd = {};
        flattenSkills(m.slots).forEach(({ skill }) => {
          const sid = skill.id || skill.skill_id;
          if (!sid) return;
          sd[sid] = {
            name:      skill.name || "",
            lvl_req:   (skill.lvl_req ?? "").toString(),
            cost:      (skill.cost ?? "").toString(),
            tagline:   skill.tagline || "",
            long_desc: skill.long_desc || "",
          };
        });
        setSkillDrafts(sd);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load codex."))
      .finally(() => setFetching(false));
  }, [classId, isOwner]);

  const skills = useMemo(() => flattenSkills(codex?.merged?.slots), [codex]);
  const canonSkills = useMemo(() => flattenSkills(codex?.canon?.slots), [codex]);
  // Build a fast "live preview" view that reflects what the player
  // WILL see once the admin hits Save. Starts from the merged doc
  // (so already-saved overrides are honored) and overlays the
  // currently-dirty metaDraft / skillDrafts on top. Any field the
  // admin is still editing shows up in the right-hand "Preview"
  // column before they commit the change.
  const previewMerged = useMemo(() => {
    const m = codex?.merged ? { ...codex.merged } : null;
    if (!m) return null;
    // Numeric and string meta drafts merge over the stored merged doc.
    const dirtyMeta = {};
    if (metaDraft.tagline    !== (m.tagline    || "")) dirtyMeta.tagline    = metaDraft.tagline;
    if (metaDraft.long_desc  !== (m.long_desc  || "")) dirtyMeta.long_desc  = metaDraft.long_desc;
    if (metaDraft.lore       !== (m.lore       || "")) dirtyMeta.lore       = metaDraft.lore;
    if (metaDraft.sprite_url !== (m.sprite_url || "")) dirtyMeta.sprite_url = metaDraft.sprite_url;
    return { ...m, ...dirtyMeta };
  }, [codex, metaDraft]);
  const previewSkillMap = useMemo(() => {
    // {skill_id: previewed_skill_dict} — flatten canon + merged + draft
    const byId = {};
    (skills || []).forEach(({ skill }) => {
      const sid = skill.id || skill.skill_id;
      if (!sid) return;
      const d = skillDrafts[sid] || {};
      byId[sid] = {
        ...skill,
        name:      d.name      !== undefined && d.name      !== "" ? d.name      : skill.name,
        tagline:   d.tagline   !== undefined && d.tagline   !== "" ? d.tagline   : skill.tagline,
        long_desc: d.long_desc !== undefined && d.long_desc !== "" ? d.long_desc : skill.long_desc,
        lvl_req:   d.lvl_req   !== undefined && d.lvl_req   !== "" ? Number(d.lvl_req) : skill.lvl_req,
        cost:      d.cost      !== undefined && d.cost      !== "" ? Number(d.cost)    : skill.cost,
      };
    });
    return byId;
  }, [skills, skillDrafts]);
  const canonSkillMap = useMemo(() => {
    const byId = {};
    (canonSkills || []).forEach(({ skill }) => {
      const sid = skill.id || skill.skill_id;
      if (sid) byId[sid] = skill;
    });
    return byId;
  }, [canonSkills]);
  const overrides = codex?.overrides || {};
  // The backend stores class-meta overrides at `overrides.class_meta`
  // and per-skill overrides at `overrides.skills.{skill_id}`. Treat
  // either shape as "has overrides" so the Reset button shows.
  const hasAnyOverrides = (codex?.merged?.overrides_active)
    || Object.keys(overrides.class_meta || overrides.meta || {}).length > 0
    || Object.keys(overrides.skills || {}).length > 0;

  const saveMeta = async () => {
    setSaving(true);
    const body = {
      tagline:    metaDraft.tagline.trim(),
      long_desc:  metaDraft.long_desc.trim(),
      lore:       metaDraft.lore.trim(),
      sprite_url: metaDraft.sprite_url.trim(),
      base_stats: {
        ...(metaDraft.block    !== "" ? { block:    Number(metaDraft.block) }    : {}),
        ...(metaDraft.charisma !== "" ? { charisma: Number(metaDraft.charisma) } : {}),
      },
    };
    try {
      await axios.patch(`${API}/admin/hero-codex/${classId}`, body, { withCredentials: true });
      toast.success(`✅ Saved ${classId} meta overrides.`);
      // Refresh so overrides_active / merged values reflect the save.
      const { data } = await axios.get(`${API}/admin/hero-codex/${classId}`, { withCredentials: true });
      setCodex(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const saveSkill = async (skillId) => {
    const d = skillDrafts[skillId] || {};
    setSaving(true);
    const body = {
      name:      d.name?.trim(),
      lvl_req:   d.lvl_req !== "" ? Number(d.lvl_req) : undefined,
      cost:      d.cost    !== "" ? Number(d.cost)    : undefined,
      tagline:   d.tagline?.trim(),
      long_desc: d.long_desc?.trim(),
    };
    try {
      await axios.patch(
        `${API}/admin/hero-codex/${classId}/skills/${skillId}`,
        body,
        { withCredentials: true },
      );
      toast.success(`✅ Saved ${skillId} override.`);
      const { data } = await axios.get(`${API}/admin/hero-codex/${classId}`, { withCredentials: true });
      setCodex(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Skill save failed.");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm(`Reset ALL ${classId} codex overrides back to canon? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/hero-codex/${classId}/overrides`, { withCredentials: true });
      toast.success(`🪣 ${classId} overrides wiped. Canon restored.`);
      const { data } = await axios.get(`${API}/admin/hero-codex/${classId}`, { withCredentials: true });
      setCodex(data);
      const m = data?.merged || {};
      setMetaDraft({
        tagline:    m.tagline || "",
        long_desc:  m.long_desc || "",
        lore:       m.lore || "",
        sprite_url: m.sprite_url || "",
        block:      (m.base_stats?.block ?? "").toString(),
        charisma:   (m.base_stats?.charisma ?? "").toString(),
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (!isOwner) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400" data-testid="codex-editor-locked">
        🔒 Owner only.
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8" data-testid="hero-codex-editor-page">
      <div className="mb-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl mb-1">📖 Hero Codex Editor</h1>
          <p className="text-xs text-neutral-500">
            Rewrite tagline, long description, lore, base stats, and every skill's blurb per class.
            Changes land for every player on next page load.
          </p>
        </div>
        <select
          data-testid="codex-editor-class-picker"
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
          className="bg-black border border-neutral-800 px-3 py-2 text-neutral-100 text-sm uppercase tracking-widest focus:border-amber-400 focus:outline-none"
        >
          {classes.map((c) => (
            <option key={c.class_id} value={c.class_id}>
              {c.icon || "🎭"} {c.name || c.class_id}
            </option>
          ))}
        </select>
      </div>

      {fetching && <div className="text-neutral-400 text-sm">Loading codex…</div>}

      {!fetching && codex && (
        <>
          {/* ===== Class Meta ===== */}
          <section className="border-2 border-neutral-800 bg-[#0F0F0F] p-5 mb-5"
                   data-testid="codex-meta-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl text-amber-300">Class Meta</h2>
              {hasAnyOverrides && (
                <span className="text-[10px] font-black uppercase tracking-widest text-amber-400 px-2 py-0.5 border border-amber-400/40">
                  ⚠ Overrides active
                </span>
              )}
            </div>
            <div className="grid gap-3">
              <label className="text-[11px] uppercase tracking-widest text-neutral-500">
                Tagline
                <input
                  type="text"
                  data-testid="codex-meta-tagline"
                  value={metaDraft.tagline}
                  onChange={(e) => setMetaDraft({ ...metaDraft, tagline: e.target.value })}
                  className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1.5 text-neutral-100 text-sm focus:border-amber-400 focus:outline-none"
                />
              </label>
              <label className="text-[11px] uppercase tracking-widest text-neutral-500">
                Long description
                <textarea
                  data-testid="codex-meta-longdesc"
                  rows={3}
                  value={metaDraft.long_desc}
                  onChange={(e) => setMetaDraft({ ...metaDraft, long_desc: e.target.value })}
                  className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1.5 text-neutral-100 text-sm focus:border-amber-400 focus:outline-none"
                />
              </label>
              <label className="text-[11px] uppercase tracking-widest text-neutral-500">
                Lore
                <textarea
                  data-testid="codex-meta-lore"
                  rows={4}
                  value={metaDraft.lore}
                  onChange={(e) => setMetaDraft({ ...metaDraft, lore: e.target.value })}
                  className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1.5 text-neutral-100 text-sm focus:border-amber-400 focus:outline-none"
                />
              </label>
              {/* 🖼️ Feb 2026 — Codex art slot. Paste any direct image URL
                 (CDN, Emergent Object Storage, local /sprites path) to
                 swap this class's codex art without a code change.
                 Leave blank to fall back to the baked canon sprite. */}
              <label className="text-[11px] uppercase tracking-widest text-neutral-500">
                Codex art URL (sprite_url)
                <div className="flex gap-2 items-start mt-1">
                  <input
                    type="url"
                    data-testid="codex-meta-sprite"
                    placeholder="https://… · leave blank to use canon"
                    value={metaDraft.sprite_url}
                    onChange={(e) => setMetaDraft({ ...metaDraft, sprite_url: e.target.value })}
                    className="flex-1 min-w-0 bg-black border border-neutral-800 px-2 py-1.5 text-neutral-100 text-sm focus:border-amber-400 focus:outline-none"
                  />
                  {metaDraft.sprite_url ? (
                    <img
                      src={metaDraft.sprite_url}
                      alt="codex art preview"
                      data-testid="codex-meta-sprite-preview"
                      onError={(e) => { e.target.style.display = "none"; }}
                      onLoad={(e) => { e.target.style.display = "block"; }}
                      className="w-14 h-14 object-cover bg-neutral-950 border border-amber-500/40 shrink-0"
                    />
                  ) : (
                    <div className="w-14 h-14 flex items-center justify-center bg-neutral-950 border border-neutral-800 text-[9px] text-neutral-600 uppercase tracking-widest shrink-0">
                      Canon
                    </div>
                  )}
                </div>
                <div className="text-[10px] text-neutral-600 mt-1 normal-case tracking-normal">
                  Leave blank to restore the baked canon sprite for this class.
                </div>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-[11px] uppercase tracking-widest text-neutral-500">
                  Block (base_stats.block)
                  <input
                    type="number"
                    data-testid="codex-meta-block"
                    value={metaDraft.block}
                    onChange={(e) => setMetaDraft({ ...metaDraft, block: e.target.value })}
                    className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1.5 text-neutral-100 text-sm focus:border-amber-400 focus:outline-none"
                  />
                </label>
                <label className="text-[11px] uppercase tracking-widest text-neutral-500">
                  Charisma (base_stats.charisma)
                  <input
                    type="number"
                    data-testid="codex-meta-charisma"
                    value={metaDraft.charisma}
                    onChange={(e) => setMetaDraft({ ...metaDraft, charisma: e.target.value })}
                    className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1.5 text-neutral-100 text-sm focus:border-amber-400 focus:outline-none"
                  />
                </label>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                data-testid="codex-meta-save"
                disabled={saving}
                onClick={saveMeta}
                className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-amber-500 text-black hover:bg-amber-300 disabled:bg-neutral-800 disabled:text-neutral-500"
              >
                {saving ? "Saving…" : "💾 Save Meta"}
              </button>
              {hasAnyOverrides && (
                <button
                  data-testid="codex-reset"
                  disabled={saving}
                  onClick={resetAll}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest border border-red-800 text-red-300 hover:bg-red-950/30 disabled:text-neutral-500"
                >
                  🪣 Reset ALL to canon
                </button>
              )}
            </div>
          </section>

          {/* ===== 🪞 Canon vs Preview diff panel ===== */}
          <CanonVsPreviewPanel
            canon={codex.canon}
            previewMerged={previewMerged}
            canonSkillMap={canonSkillMap}
            previewSkillMap={previewSkillMap}
            skills={skills}
          />

          {/* ===== Skills ===== */}
          <section className="space-y-3" data-testid="codex-skills-section">
            <h2 className="font-display text-xl text-amber-300">Skills ({skills.length})</h2>
            {skills.map(({ slot, idx, skill }) => {
              const sid = skill.id || skill.skill_id;
              if (!sid) return null;
              const d = skillDrafts[sid] || {};
              const slotLabel = idx === null ? slot : `${slot}#${idx + 1}`;
              return (
                <div
                  key={sid}
                  data-testid={`codex-skill-${sid}`}
                  className="border border-neutral-800 bg-[#0F0F0F] p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{skill.icon || "✨"}</span>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-neutral-500">{slotLabel}</div>
                        <div className="font-sans font-bold text-white text-sm">{d.name || skill.name || sid}</div>
                      </div>
                    </div>
                    <code className="text-[10px] text-neutral-600">{sid}</code>
                  </div>
                  <div className="grid gap-2">
                    <div className="grid grid-cols-3 gap-2">
                      <label className="text-[10px] uppercase tracking-widest text-neutral-500 col-span-1">
                        Name
                        <input
                          type="text"
                          data-testid={`codex-skill-name-${sid}`}
                          value={d.name || ""}
                          onChange={(e) => setSkillDrafts({ ...skillDrafts, [sid]: { ...d, name: e.target.value } })}
                          className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1 text-neutral-100 text-xs focus:border-amber-400 focus:outline-none"
                        />
                      </label>
                      <label className="text-[10px] uppercase tracking-widest text-neutral-500">
                        Lv Req
                        <input
                          type="number"
                          data-testid={`codex-skill-lvl-${sid}`}
                          value={d.lvl_req ?? ""}
                          onChange={(e) => setSkillDrafts({ ...skillDrafts, [sid]: { ...d, lvl_req: e.target.value } })}
                          className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1 text-neutral-100 text-xs focus:border-amber-400 focus:outline-none"
                        />
                      </label>
                      <label className="text-[10px] uppercase tracking-widest text-neutral-500">
                        Cost (mana/rage)
                        <input
                          type="number"
                          data-testid={`codex-skill-cost-${sid}`}
                          value={d.cost ?? ""}
                          onChange={(e) => setSkillDrafts({ ...skillDrafts, [sid]: { ...d, cost: e.target.value } })}
                          className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1 text-neutral-100 text-xs focus:border-amber-400 focus:outline-none"
                        />
                      </label>
                    </div>
                    <label className="text-[10px] uppercase tracking-widest text-neutral-500">
                      Tagline
                      <input
                        type="text"
                        data-testid={`codex-skill-tagline-${sid}`}
                        value={d.tagline || ""}
                        onChange={(e) => setSkillDrafts({ ...skillDrafts, [sid]: { ...d, tagline: e.target.value } })}
                        className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1 text-neutral-100 text-xs focus:border-amber-400 focus:outline-none"
                      />
                    </label>
                    <label className="text-[10px] uppercase tracking-widest text-neutral-500">
                      Long description
                      <textarea
                        data-testid={`codex-skill-longdesc-${sid}`}
                        rows={2}
                        value={d.long_desc || ""}
                        onChange={(e) => setSkillDrafts({ ...skillDrafts, [sid]: { ...d, long_desc: e.target.value } })}
                        className="mt-1 w-full bg-black border border-neutral-800 px-2 py-1 text-neutral-100 text-xs focus:border-amber-400 focus:outline-none"
                      />
                    </label>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <button
                      data-testid={`codex-skill-save-${sid}`}
                      disabled={saving}
                      onClick={() => saveSkill(sid)}
                      className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border border-amber-500 text-amber-300 hover:bg-amber-500 hover:text-black disabled:border-neutral-800 disabled:text-neutral-500"
                    >
                      💾 Save Skill
                    </button>
                  </div>
                </div>
              );
            })}
            {skills.length === 0 && (
              <div className="text-neutral-500 text-sm italic text-center py-4">
                No skills registered for this class yet.
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* 🪞 Canon vs Preview — side-by-side diff panel                   */
/* ─────────────────────────────────────────────────────────────── */
/**
 * Renders two columns:
 *  • LEFT  — baked canon (immutable truth, dim/muted).
 *  • RIGHT — "Preview" view = merged doc overlaid with the admin's
 *            current drafts. Every field the admin has changed (saved
 *            override OR unsaved draft) shows an amber "edited" ring
 *            so the diff is obvious at a glance.
 *
 * This is READ-ONLY — the real editable inputs live above/below. The
 * panel exists so you can SEE the net effect of overrides on the
 * player-facing codex before you hit Save.
 */
function CanonVsPreviewPanel({ canon, previewMerged, canonSkillMap, previewSkillMap, skills }) {
  if (!canon || !previewMerged) return null;

  const diff = (c, p) => (String(c ?? "") !== String(p ?? ""));

  const Field = ({ label, canonVal, previewVal, pre = false }) => {
    const changed = diff(canonVal, previewVal);
    return (
      <div className="grid grid-cols-2 gap-2 py-1.5 border-b border-neutral-900">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-600 mb-0.5">
            {label}
          </div>
          <div className={`text-[11px] text-neutral-400 ${pre ? "whitespace-pre-wrap" : ""}`}>
            {canonVal || <span className="italic text-neutral-700">—</span>}
          </div>
        </div>
        <div className={changed ? "ring-1 ring-amber-400/60 bg-amber-500/5 px-1.5 -mx-1.5 py-1 -my-1" : ""}>
          <div className={`text-[9px] uppercase tracking-widest mb-0.5 ${changed ? "text-amber-300" : "text-neutral-600"}`}>
            {label}{changed && " · changed"}
          </div>
          <div className={`text-[11px] ${pre ? "whitespace-pre-wrap" : ""} ${changed ? "text-amber-100 font-bold" : "text-neutral-400"}`}>
            {previewVal || <span className="italic text-neutral-700">—</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section
      className="border-2 border-amber-500/40 bg-gradient-to-b from-amber-950/10 to-[#0F0F0F] p-5 mb-5"
      data-testid="codex-preview-panel"
    >
      <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-xl text-amber-300">🪞 Canon vs Preview</h2>
          <p className="text-[11px] text-neutral-500">
            Left = baked canon · Right = what the player will see once you save.
            Changed fields are ringed in amber.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 text-[9px] uppercase tracking-widest text-neutral-500">
          <span className="text-center">Canon</span>
          <span className="text-center text-amber-300">Preview</span>
        </div>
      </div>

      {/* Sprite side-by-side */}
      <div className="grid grid-cols-2 gap-2 py-1.5 border-b border-neutral-900">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-600 mb-0.5">Codex Art</div>
          {canon.sprite_url ? (
            <img
              src={canon.sprite_url}
              alt="canon sprite"
              data-testid="codex-preview-canon-sprite"
              onError={(e) => { e.target.style.display = "none"; }}
              className="w-20 h-20 object-cover bg-neutral-950 border border-neutral-800"
            />
          ) : (
            <div className="w-20 h-20 flex items-center justify-center bg-neutral-950 border border-neutral-800 text-[9px] text-neutral-600 italic">
              none
            </div>
          )}
        </div>
        <div className={diff(canon.sprite_url, previewMerged.sprite_url) ? "ring-1 ring-amber-400/60 bg-amber-500/5 px-1.5 -mx-1.5 py-1 -my-1" : ""}>
          <div className={`text-[9px] uppercase tracking-widest mb-0.5 ${diff(canon.sprite_url, previewMerged.sprite_url) ? "text-amber-300" : "text-neutral-600"}`}>
            Codex Art{diff(canon.sprite_url, previewMerged.sprite_url) && " · changed"}
          </div>
          {previewMerged.sprite_url ? (
            <img
              src={previewMerged.sprite_url}
              alt="preview sprite"
              data-testid="codex-preview-sprite"
              onError={(e) => { e.target.style.display = "none"; }}
              className={`w-20 h-20 object-cover bg-neutral-950 border ${diff(canon.sprite_url, previewMerged.sprite_url) ? "border-amber-400" : "border-neutral-800"}`}
            />
          ) : (
            <div className="w-20 h-20 flex items-center justify-center bg-neutral-950 border border-neutral-800 text-[9px] text-neutral-600 italic">
              none
            </div>
          )}
        </div>
      </div>

      <Field label="Tagline"          canonVal={canon.tagline}    previewVal={previewMerged.tagline} />
      <Field label="Long description" canonVal={canon.long_desc}  previewVal={previewMerged.long_desc} pre />
      <Field label="Lore"             canonVal={canon.lore}       previewVal={previewMerged.lore} pre />
      <Field label="Block"            canonVal={canon.stats?.block}    previewVal={previewMerged.stats?.block} />
      <Field label="Charisma"         canonVal={canon.stats?.charisma} previewVal={previewMerged.stats?.charisma} />

      {/* Skill-by-skill rows (only shown when at least one differs) */}
      {skills.length > 0 && (
        <div className="mt-4 pt-3 border-t border-amber-500/30">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2">
            Skills preview
          </div>
          <div className="space-y-1.5">
            {skills.map(({ slot, idx, skill }) => {
              const sid = skill.id || skill.skill_id;
              if (!sid) return null;
              const canonSk   = canonSkillMap[sid]   || {};
              const previewSk = previewSkillMap[sid] || {};
              const changed =
                diff(canonSk.name,      previewSk.name) ||
                diff(canonSk.lvl_req,   previewSk.lvl_req) ||
                diff(canonSk.cost,      previewSk.cost) ||
                diff(canonSk.tagline,   previewSk.tagline) ||
                diff(canonSk.long_desc, previewSk.long_desc);
              const slotLabel = idx === null ? slot : `${slot}#${idx + 1}`;
              return (
                <div
                  key={sid}
                  data-testid={`codex-preview-skill-${sid}`}
                  className={`grid grid-cols-2 gap-2 text-[10px] px-2 py-1 ${changed ? "bg-amber-500/5 border-l-2 border-amber-400" : "border-l-2 border-neutral-900"}`}
                >
                  <div>
                    <div className="text-[9px] text-neutral-600 uppercase tracking-widest">{slotLabel}</div>
                    <div className="text-neutral-300 font-sans">
                      {canonSk.name || sid}
                      <span className="text-neutral-600"> · Lv {canonSk.lvl_req ?? "?"} · cost {canonSk.cost ?? "?"}</span>
                    </div>
                    <div className="text-neutral-500 italic">{canonSk.tagline || "—"}</div>
                  </div>
                  <div>
                    <div className={`text-[9px] uppercase tracking-widest ${changed ? "text-amber-300 font-bold" : "text-neutral-600"}`}>
                      {slotLabel}{changed && " · changed"}
                    </div>
                    <div className={`font-sans ${changed ? "text-amber-100 font-bold" : "text-neutral-300"}`}>
                      {previewSk.name || sid}
                      <span className={changed ? "text-amber-300" : "text-neutral-600"}>
                        {" "}· Lv {previewSk.lvl_req ?? "?"} · cost {previewSk.cost ?? "?"}
                      </span>
                    </div>
                    <div className={`italic ${changed ? "text-amber-200" : "text-neutral-500"}`}>
                      {previewSk.tagline || "—"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

